/**
 * Mutaciones del feature prorrateo · cada una encapsula su efecto:
 *   1. Llama al endpoint correspondiente.
 *   2. Hidrata el cache local con la respuesta del backend (`setQueryData`)
 *      para que la UI refleje el cambio sin un refetch redundante.
 *   3. Invalida la query de la solicitud (el estado del agregado pudo cambiar
 *      con la mutación · ej. confirmar prorrateo avanza el estado).
 *   4. Notifica al usuario con un mensaje consistente extraído por `toastErrorApi`.
 *
 * Ningún componente toca `queryClient` directamente · solo invoca el hook
 * y consume `mutate` / `isPending` / `error`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { prorrateoApi, type FilaDispersionInput } from "@/features/prorrateo/services/prorrateo.api";
import type { SubLineaUpdate, LineaManualInput, Prorrateo } from "@/features/prorrateo/types";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";

function useProrrateoQueryHelpers(solicitudId: string) {
  const qc = useQueryClient();
  const onSuccess = (data: Prorrateo, title: string, message?: string) => {
    // Cache del prorrateo single (CFDI único) + invalidación de la lista (Múltiples CFDI) ·
    // ambas conviven sin estorbarse: la que no esté montada simplemente no refetchea.
    qc.setQueryData(qk.prorrateo(solicitudId), data);
    qc.invalidateQueries({ queryKey: qk.prorrateos(solicitudId) });
    qc.invalidateQueries({ queryKey: qk.solicitud(solicitudId) });
    toastExito({ title, message: message ?? "Cambios guardados." });
  };
  return { onSuccess, onError: toastErrorApi };
}

export function useConfirmarProrrateo(solicitudId: string) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: () => prorrateoApi.confirmar(solicitudId),
    onSuccess: (data) =>
      onSuccess(data, "Prorrateo confirmado", "Ahora adjunta los documentos de soporte."),
    onError,
  });
}

export function useReabrirProrrateo(solicitudId: string) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: () => prorrateoApi.reabrir(solicitudId),
    onSuccess: (data) =>
      onSuccess(data, "Prorrateo reabierto", "Puedes editar las líneas y volver a confirmar."),
    onError,
  });
}

export function useAgregarLineaManual(solicitudId: string) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: (input: LineaManualInput) =>
      prorrateoApi.agregarLineaManual(solicitudId, input),
    onSuccess: (data) => onSuccess(data, "Línea agregada"),
    onError,
  });
}

export function useEditarSubLinea(solicitudId: string, numeroCfdi?: number) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: ({ lineaId, update }: { lineaId: string; update: SubLineaUpdate }) =>
      prorrateoApi.editarSubLinea(solicitudId, lineaId, update, numeroCfdi),
    onSuccess: (data) => onSuccess(data, "Sub-línea actualizada"),
    onError,
  });
}

export function useDividirConcepto(solicitudId: string, numeroCfdi?: number) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: (conceptoNumero: number) => prorrateoApi.dividirConcepto(solicitudId, conceptoNumero, numeroCfdi),
    onSuccess: (data) => onSuccess(data, "Concepto dividido", "Reparte el importe entre las sub-líneas."),
    onError,
  });
}

export function useRestablecerIndicadores(solicitudId: string, numeroCfdi?: number) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: () => prorrateoApi.restablecerIndicadores(solicitudId, numeroCfdi),
    onSuccess: (data) =>
      onSuccess(data, "Indicadores restablecidos", "Cada línea tiene ahora el indicador de impuestos de su concepto."),
    onError,
  });
}

export function useEditarLineaManual(solicitudId: string) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: ({ lineaId, input }: { lineaId: string; input: LineaManualInput }) =>
      prorrateoApi.editarLineaManual(solicitudId, lineaId, input),
    onSuccess: (data) => onSuccess(data, "Línea actualizada"),
    onError,
  });
}

/**
 * Carga la dispersión de CeCos desde una plantilla Excel · reemplaza las líneas
 * del prorrateo con el desglose del archivo (paridad legado FB60/Contrato).
 */
export function useImportarDispersion(solicitudId: string) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: (filas: FilaDispersionInput[]) => prorrateoApi.importarDispersion(solicitudId, filas),
    onSuccess: (data) =>
      onSuccess(data, "Dispersión cargada", "Revisa las líneas y confirma el prorrateo."),
    onError,
  });
}

export function useEliminarLinea(solicitudId: string, numeroCfdi?: number) {
  const { onSuccess, onError } = useProrrateoQueryHelpers(solicitudId);
  return useMutation({
    mutationFn: (lineaId: string) => prorrateoApi.eliminarLinea(solicitudId, lineaId, numeroCfdi),
    onSuccess: (data) => onSuccess(data, "Línea eliminada"),
    onError,
  });
}

/**
 * Re-deriva cuentas faltantes al abrir el prorrateo (transparente · sin toast):
 * si el admin ya agregó el mapeo solicitado, la línea "Sin cuenta" toma su
 * cuenta sola. Solo actualiza el cache (silencioso) · un error tampoco molesta
 * al usuario (es una mejora oportunista, no una acción que él pidió).
 */
export function useRederivarCuentasFaltantes(solicitudId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => prorrateoApi.rederivarCuentas(solicitudId),
    onSuccess: (data) => {
      qc.setQueryData(qk.prorrateo(solicitudId), data);
      qc.invalidateQueries({ queryKey: qk.solicitud(solicitudId) });
    },
  });
}
